import { Node, ZoomTo, useReactFlow } from "reactflow";
import { useHomeUsePluginContainer } from "./container";
import { find, isArray, get, isString, map } from "lodash";
import { useCallback } from "react";
import { useSetCenter } from "@custom-hooks/useSetCenter";
import { useFitCanvas } from "@uikit/hooks/useFitCanvas";

type NodeData = Node<any>;

class NodeDataList {
  nodes: NodeData[];
  constructor(nodes: NodeData[]) {
    this.nodes = nodes;
  }

  setNodes(nodes: NodeData[]) {
    this.nodes = nodes;
    return this;
  }
  getNodes() {
    return this.nodes;
  }

  getNodeById(id: string) {
    return this.nodes.filter(item => item.id === id)?.[0];
  }

  getNodesByParentId(pid: string) {
    return this.nodes.filter(item => item.parentNode === pid);
  }

  removeChildNode(parentId: string, targetChildId: string) {
    const parentNode = this.getNodeById(parentId);
    if (!parentNode || parentNode.type !== "customGroup") {
      return;
    }

    const parentChildNodes = get(parentNode, "data.childNode", []);
    if (parentNode && isArray(parentChildNodes) && parentChildNodes.length) {
      parentNode.data.childNode = parentChildNodes.filter(item => item !== targetChildId);
    }
  }

  /**
   * 移除节点用ID
   * @param id
   * @param withGroup
   * @returns
   */
  removeById(id: string, withGroup = true) {
    const target = this.getNodeById(id);

    if (!target) {
      return this;
    }

    // remove `childNode`
    if (target.parentNode) {
      this.removeChildNode(target.parentNode, id);
    }

    this.nodes = this.nodes.filter(item => item.id !== id);

    return this;
  }

  removeByIds(ids: string[], withGroup = true) {
    ids.forEach(id => {
      if (isString(id) && id) {
        this.removeById(id, withGroup);
      }
    });
    return this;
  }
}

/**
 * 为 画布提供 node,edgs 相关逻辑方法,一部分为了扩展 `useRectFlow`
 *
 * 可将原有使用 `useReactFlow` 改为 `useHomeReactFlow`
 *
 * 已有方法:
 *
 * - `deleteNodeByIds` 节点id删除多个节点
 * - `deleteNodeById` 节点id删除一个节点
 * - `zoomTo`
 *
 * @returns
 */
export function useHomeReactFlow() {
  const { nodesState, allowZoomOrPan, homeReactFlowState } = useHomeUsePluginContainer();
  const { getNodes, setNodes } = nodesState;
  const reactFlow = useReactFlow();
  const setCenterResult = useSetCenter(reactFlow);

  const deleteNodeByIds = useCallback((ids: string[]) => {
    // 将可能是组节点中的 childNode 移除
    setNodes(new NodeDataList(getNodes()).removeByIds(ids).getNodes());

    // 删除 react-flow 的节点
    const nodeIds = ids.map(id => ({ id }));
    reactFlow.deleteElements({ nodes: nodeIds });
  }, []);

  const deleteNodeById = useCallback((id: string) => {
    // 将可能是组节点中的 childNode 移除
    setNodes(new NodeDataList(getNodes()).removeById(id).getNodes());

    // 删除 react-flow 的节点
    reactFlow.deleteElements({ nodes: [{ id }] });
  }, []);

  const zoomTo: ZoomTo = (zoomLevel, options) => {
    if (!allowZoomOrPan) {
      return;
    }
    reactFlow.zoomTo(zoomLevel, options);
  };

  return {
    ...reactFlow,
    // overried
    zoomTo,
    // extends
    deleteNodeById,
    deleteNodeByIds,
    homeReactFlowState,
    ...setCenterResult,
  };
}
